import {
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    OnDestroy,
    ViewChild,
    AfterViewInit,
    signal,
    Inject,
    PLATFORM_ID,
    afterNextRender,
    ChangeDetectorRef,
} from '@angular/core';
import {
    getLanguageOptions,
    getLanguageTranslation,
    getLongLanguageOptions,
    Language,
    LANGUAGE_SELECTIONS,
} from '../../constants/ui-utils.constant';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SIDE_MENU_ITEMS } from '../../constants/menu.constant';
import { AccountService } from '../../../features/authentication/services/account.service';
import { UserDetailsInterface } from '../../models/user.model';
import { UsersService } from '../../../features/authentication/services/api/users.service';
import { filter, finalize, firstValueFrom, Subject, takeUntil, tap } from 'rxjs';
import { ConfirmationPopupInterface } from '../../libs/shared/components/confirmation-popup/confirmation-popup.model';
import { BlockUiService } from '../../libs/shared/components/block-ui/block-ui.service';
import { ERR_ASKPOD_NOT_ELIGIBLE, ERR_ASKPOD_CONNECT_FAILED, SCREEN_WIDTH_DESKTOP, SEARCH_MAX_LENGTH } from '../../constants/app.constant';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../services/api/notification.service';
import { ErrorService } from '../../services/error.service';
import { DialogService } from '../../services/dialog.service';
import { JourneyListSequence } from '../journey-block-ui/journey-block-ui.component';
import { StorageService } from '../../services/storage.service';
import { MultilingualService } from '../../services/api/multilingual.service';
import { MultilingualDropdownModel } from '../../models/multilingual.model';
import { MatMenuTrigger } from '@angular/material/menu';
import { MenuTriggerService } from './menu-trigger.service';
import { LanguageLocal } from '../../enum/ui-utils.enum';
import { isPlatformBrowser } from '@angular/common';
import { AskpodService } from '../../services/api/askpod.service';
import { SystemSettingService } from '../../services/api/system-setting.service';
import { CustomToastrService } from '../../services/custom-toastr.service';
import { TrackingEventType, TrackingService } from '../../services/api/tracking.service';
import { isAbsoluteHttpUrl } from '../../helpers/string-utils';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('searchPopup') searchPopup!: ElementRef;
    @ViewChild('searchPopupMobile') searchPopupMobile!: ElementRef;
    @ViewChild('searchInput') searchInput!: ElementRef;
    @ViewChild('searchInputMobile') searchInputMobile!: ElementRef;
    @ViewChild('notification') notification!: ElementRef;
    @ViewChild('notificationButton') notificationButton!: ElementRef;
    @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

    @Input() isAuthLayout = false;
    @Input() showLanguageDropdown = true;

    private destroy$ = new Subject<void>();

    protected availableLanguage: MultilingualDropdownModel[] = [];
    protected firstTimeJourneyStepsParam = 0;
    protected isAskpodDisabled = false;
    /**
     * Backend-controlled visibility for the askPOD entry point (both the desktop button and the
     * mobile-breakpoint button). Distinct from `isAskpodDisabled`: this HIDES the control entirely
     * because the feature is switched off for everyone, whereas `isAskpodDisabled` greys out a
     * visible control for the current user/attempt.
     *
     * Named to match the backend field it mirrors (`isAskPodFeatureEnabled` on the feature-flag
     * response) so the two are greppable as one thing. Fetched by `initAskpodVisibility()`.
     *
     * Starts false, which matters in three situations that all resolve to "hidden":
     * - during SSR, where ngOnInit returns early, so the server markup and the client's first
     *   render agree and hydration has nothing to reconcile;
     * - for anonymous visitors (the public `clips/preview/:id` page renders this header), for whom
     *   the flag is never fetched — and who could not use askPOD anyway, since the SSO endpoint
     *   requires an authenticated Client role;
     * - if the config call fails.
     *
     * It flips to true shortly after load when the feature is on, the same async-fill behaviour as
     * `unreadNotificationCount`.
     */
    protected isAskPodFeatureEnabled = false;
    protected isAskpodLoading = false;
    protected isChangingLanguage = false;
    protected isClientSide = signal(false);
    protected isFirstTimeJourney = false;
    protected isMobileMenuOpen = false;
    protected isMobileView = false;
    protected isNotificationOpen = false;
    protected isSearchPopupVisible = false;
    protected isToggleNotification = false;
    protected isUserAuthenticated = signal(false);
    protected searchInputValue = '';
    protected selectedLanguage = '';
    protected showDemoButton = false;
    protected unreadNotificationCount = 0;
    protected userDetails: UserDetailsInterface = {} as UserDetailsInterface;

    protected Language = Language;

    protected LANGUAGE_SELECTIONS = LANGUAGE_SELECTIONS;
    protected SEARCH_MAX_LENGTH = SEARCH_MAX_LENGTH;
    protected SIDE_MENU_ITEMS = SIDE_MENU_ITEMS;

    protected getLanguageOptions = getLanguageOptions;
    protected getLongLanguageOptions = getLongLanguageOptions;
    protected getLanguageTranslation = getLanguageTranslation;

    constructor(
        private accountService: AccountService,
        private activatedRoute: ActivatedRoute,
        private blockUiService: BlockUiService,
        private cdr: ChangeDetectorRef,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private multilingualService: MultilingualService,
        private notificationService: NotificationService,
        private router: Router,
        private storageService: StorageService,
        private translateService: TranslateService,
        private userService: UsersService,
        private menuTriggerService: MenuTriggerService,
        private askpodService: AskpodService,
        private systemSettingService: SystemSettingService,
        private customToastrService: CustomToastrService,
        private trackingService: TrackingService,
        @Inject(PLATFORM_ID) private platformId: object
    ) {
        afterNextRender(() => {
            // Set authentication state after hydration to prevent mismatch
            this.isUserAuthenticated.set(this.accountService.isAuthenticated());
        });

        // Consolidated client-side initialization.
        // Developer Note: We cannot utilize "afterNextRender"
        // for this segment as the API calls triggered after the first
        // render would invoke NG0100 error.
        // For more info, refer to this link:
        // https://angular.dev/errors/NG0100
        this.isClientSide.set(isPlatformBrowser(this.platformId));
    }

    @HostListener('document:click', ['$event.target'])
    onClickOutside(target: HTMLElement): void {
        if (
            this.isSearchPopupVisible &&
            !(this.isMobileView ? this.searchPopupMobile : this.searchPopup)?.nativeElement.contains(target)
        ) {
            this.isSearchPopupVisible = false;
        }

        if (!this.isSearchPopupVisible && this.searchInput?.nativeElement.contains(target) && !this.isMobileView) {
            this.isSearchPopupVisible = true;
        }

        if (!this.isSearchPopupVisible && this.searchInputMobile?.nativeElement.contains(target) && this.isMobileView) {
            this.isSearchPopupVisible = true;
        }

        if (this.notificationButton?.nativeElement.contains(target)) {
            this.triggerNotification();
        }
        else {
            if (this.isNotificationOpen) {
                if (!this.notification.nativeElement.contains(target)) {
                    this.triggerNotification();
                }
            }
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(): void {
        this.isMobileView = window.innerWidth < SCREEN_WIDTH_DESKTOP;
        this.isMobileMenuOpen = window.innerWidth < SCREEN_WIDTH_DESKTOP && this.isFirstTimeJourney;
        this.isSearchPopupVisible = false;

        if (Number(this.firstTimeJourneyStepsParam) >= JourneyListSequence.Search) {
            this.isMobileMenuOpen = false;
        }
    }

    async ngOnInit(): Promise<void> {
        this.initAvailableLanguage();

        if (!this.isClientSide())
            return;

        this.onResize();

        // Subscribe to authentication state changes
        this.accountService.userDetails
            .pipe(takeUntil(this.destroy$))
            .subscribe((userDetails) => {
                this.isUserAuthenticated.set(!!userDetails || this.accountService.isAuthenticated());
            });

        this.initRouterChangeEvents();
        //this.initFirstTimeJourneyUi();
        this.initHeaderUI();
        this.initRefreshNotificationCounterOnRead();

        if (!this.isAuthLayout) {
            await this.getUnreadNotificationCount();
            await this.getUserPreference();
        }

        if (this.accountService.isAuthenticated()) {
            // Gated on authentication because the endpoint is [AuthorizeRoles(Client)]. Calling it
            // anonymously would 401 — harmless (it fails closed) but it would log an error on every
            // load of the public clips/preview page, which renders this same header.
            //
            // Not awaited: it only decides whether one button renders, so it must not delay the
            // profile fetch below.
            this.initAskpodVisibility();

            await this.getUserDetails();
        }
    }

    ngAfterViewInit(): void {
        this.registerMenuTriggerListener();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected async changeSelectedLanguage(selectedLanguage: string): Promise<void> {
        // Immediately update the selected language to reflect user choice
        this.selectedLanguage = selectedLanguage;
        
        if (this.isAuthLayout) {
            this.translateService.use(selectedLanguage);
            this.storageService.setLanguage(selectedLanguage);
            this.multilingualService.currentLanguage.next(selectedLanguage);
        }
        else {
            let language: Language;

            if (selectedLanguage === LanguageLocal.LanguageCN) {
                language = Language.CH;
            }
            else {
                language = Language.EN;
            }

            // TPOD-2263: applying the language fetches the translation file over
            // HTTP, which is noticeable for zh-CN. Block the UI with a centred
            // spinner (same pattern as the auto-enrol overlay) until the new
            // language has been applied, so the switch does not look frozen.
            this.isChangingLanguage = true;

            this.userService
                .updatePreference({ language })
                .pipe(finalize(() => this.getUserPreference().finally(() => (this.isChangingLanguage = false))))
                .subscribe();
        }
    }

    protected onSearch(data: string): void {
        const sanitizedKeyword = data.substring(0, SEARCH_MAX_LENGTH);

        this.router.navigate(['/search'], { queryParams: { keyword: sanitizedKeyword } });

        this.isSearchPopupVisible = false;
    }

    protected onFilterClear(): void {
        this.searchInputValue = '';
        (this.isMobileView ? this.searchInputMobile : this.searchInput).nativeElement.value = '';
    }

    protected onSuggestedKeyword(keyword: string): void {
        const sanitizedKeyword = keyword.substring(0, SEARCH_MAX_LENGTH);

        this.searchInputValue = sanitizedKeyword;
        (this.isMobileView ? this.searchInputMobile : this.searchInput).nativeElement.value = sanitizedKeyword;

        this.router.navigate(['/search'], { queryParams: { keyword: sanitizedKeyword } });
    }

    protected logout(): void {
        const confirmationBody: ConfirmationPopupInterface = {
            title: 'confirmation.logout',
            message: 'confirmation.areYouSureYouWantToLogout',
            cancelText: 'button.cancel',
        };

        this.dialogService
            .showConfirmationPopup(confirmationBody)
            .pipe(
                filter((isConfirm) => isConfirm),
                tap(() => {
                    // FIX: Set logout flag to prevent guards from fetching user details during logout
                    this.accountService.isLoggingOut = true;
                    this.accountService.userDetails.next(null);

                    this.userService.logout().pipe(
                        finalize(() => {
                            this.accountService.completeLogout();
                        })
                    ).subscribe();
                }),
                finalize(() => {
                    this.blockUiService.close();
                }),
            )
            .subscribe();
    }

    protected closeMobileMenu(closeMenu: boolean): void {
        this.isMobileMenuOpen = !closeMenu;
    }

    protected onHotTopicClicked(): void {
        this.onFilterClear();

        this.isSearchPopupVisible = false;
    }

    protected onAskPodClicked(): void {
        // Trigger askPOD click tracking event.
        this.trackingService.trackEvent(TrackingEventType.AskpodIconClick);

        if (this.isAskpodDisabled || this.isAskpodLoading) return;

        // No tab is opened here — launchAskpod() runs the full gate first and only opens a
        // tab once every check clears, so a blocked entry (Trial Ended / Connection down)
        // never leaves a stray blank tab behind.
        this.isAskpodLoading = true;

        this.launchAskpod();
    }

    /** Shows "Your askPOD Trial Has Ended" modal (used when the active campaign has expired). */
    protected openAskPodTrialEndedModal(): void {
        this.dialogService.openAskPodTrialEndedPopup().afterClosed().pipe(takeUntil(this.destroy$)).subscribe();
    }

    /**
     * Launches the askPOD entry. The BACKEND is the single source of truth for BOTH eligibility AND
     * askPOD reachability — this method no longer probes anything client-side. It asks the backend to
     * mint an SSO link and reacts to the backend's answer. NOTHING is opened until the call succeeds,
     * so a blocked entry never leaves a stray blank tab behind:
     *
     *   - SSO minted + absolute URL                   → open askPOD in a new tab.
     *   - backend denies eligibility                  → "Trial Has Ended" modal.
     *     (403 / ERR_ASKPOD_NOT_ELIGIBLE)
     *   - backend can't reach askPOD / misconfig      → downtime modal.
     *     (ERR_ASKPOD_CONNECT_FAILED, or any other 403)
     *   - anything else                               → generic error toast.
     *
     *   The sso-link endpoint contacts askPOD itself (it fetches active campaigns before minting), so
     *   an askPOD outage surfaces as ERR_ASKPOD_CONNECT_FAILED from the backend — no separate
     *   client-side health probe is needed. Note this proves backend→askPOD reachability, not
     *   browser→askPOD; a user whose own network can't reach askPOD would still open a broken tab.
     *
     *   Because the tab is opened only after this async call (i.e. outside the original click
     *   gesture), the browser pop-up blocker may block it — that case surfaces the "pop-up blocked"
     *   toast so it's never a silent no-op.
     */
    private launchAskpod(): void {
        this.askpodService
            .getSsoLink()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isAskpodLoading = false))
            )
            .subscribe({
                next: (response) => {
                    const url = response?.result;

                    if (url && isAbsoluteHttpUrl(url)) {
                        // Every check passed — open askPOD in a new tab. `noopener` can't be used
                        // (it makes window.open return null), so we null out `opener` manually to
                        // prevent the new tab from reaching back into this window.
                        const popup = window.open(url, '_blank');
                        if (popup) popup.opener = null;
                        else {
                            // Pop-up blocked (opened outside the original click gesture) — surface it
                            // instead of silently doing nothing.
                            this.customToastrService.showError(this.translateService.instant('askPodPopup.popupBlocked'));
                        }
                    }
                    else {
                        this.customToastrService.showError(
                            this.translateService.instant('error.somethingWentWrongPleaseTryAgain')
                        );
                    }
                },
                error: (err) => {
                    const errorCode = err?.error?.error?.code;

                    // Backend eligibility gate (single source of truth) denied access — show the
                    // trial-ended modal. When the Whitelabels:AskPod:BypassAccessGate flag is enabled,
                    // the backend returns a link instead and this branch is never taken.
                    if (errorCode === ERR_ASKPOD_NOT_ELIGIBLE) {
                        this.openAskPodTrialEndedModal();
                        return;
                    }

                    // askPOD unreachable from the backend / misconfigured (also the legacy 403 shape).
                    if (errorCode === ERR_ASKPOD_CONNECT_FAILED || err?.status === 403) {
                        this.openAskPodDowntimeModal();
                        return;
                    }

                    this.customToastrService.showError(
                        this.translateService.instant('error.somethingWentWrongPleaseTryAgain')
                    );
                },
            });
    }

    private openAskPodDowntimeModal(): void {
        this.dialogService
            .openAskPodDowntimePopup()
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((isRetry) => {
                if (isRetry) this.onAskPodClicked();
            });
    }

    protected triggerNotification(): void {
        if (this.isNotificationOpen) {
            this.isNotificationOpen = !this.isNotificationOpen;

            setTimeout(() => {
                this.isToggleNotification = !this.isToggleNotification;
            }, 100);
        }
        else {
            this.isToggleNotification = !this.isToggleNotification;

            setTimeout(() => {
                this.isNotificationOpen = this.isToggleNotification;
            }, 100);
        }
    }

    private async getUserPreference(): Promise<void> {
        // TPOD-2263: apply the user's locale reactively (same approach as the
        // auth-layout). No full-page reload — a reload re-runs the landing page's
        // ngOnInit and re-opens the announcement carousel a second time.
        const resp = await firstValueFrom(this.userService.getPreference());

        const userLang = resp.result.length && resp.result[0].language ? resp.result[0].language : Language.EN;
        const locale = userLang === Language.CH ? LanguageLocal.LanguageCN : LanguageLocal.LanguageEN;

        await firstValueFrom(
            this.translateService.use(locale).pipe(tap(() => this.storageService.setLanguage(locale))),
        );
        this.multilingualService.currentLanguage.next(locale);
        // TPOD-2263: keep the header language dropdown label in sync with the
        // applied locale. The old full-page reload re-ran ngOnInit and rebuilt
        // this from storage; without it the label would stay on the locale that
        // was active before the preference was applied (e.g. shows "EN" while the
        // UI is Chinese for a returning user whose stored locale was still en-US).
        this.selectedLanguage = locale;
    }

    private initRouterChangeEvents(): void {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                // Close Search On Route Change
                this.searchInputValue = '';
                this.isSearchPopupVisible = false;

                const inputRef = this.isMobileView ? this.searchInputMobile : this.searchInput;

                if (inputRef) {
                    inputRef.nativeElement.value = '';
                }

                this.updateDemoButtonVisibilityState();
            }
        });
    }

    /**
     * Resolves whether the askPOD entry point should be shown at all.
     *
     * The flag lives in the backend's SystemSetting table so it can be switched without a frontend
     * deploy. Browser-only by design: the header's ngOnInit returns early during SSR, so the server
     * renders the button hidden and the client's first render agrees — no hydration mismatch and no
     * API call added to page rendering. The button then appears once this resolves.
     *
     * The service already fails closed, so a failed or unauthorized read leaves the button hidden.
     */
    private initAskpodVisibility(): void {
        this.systemSettingService
            .getConfig()
            .pipe(takeUntil(this.destroy$))
            .subscribe((config) => {
                this.isAskPodFeatureEnabled = config.isAskPodFeatureEnabled;
            });
    }

    private initRefreshNotificationCounterOnRead(): void {
        this.notificationService.notificationRefreshOnRead.subscribe((resp) => {
            if (resp) {
                this.notificationService.notificationRefreshOnRead.next(false);
                this.getUnreadNotificationCount();
            }
        });
    }

    private initAvailableLanguage(): void {
        // Set default language immediately if no cached language exists
        // This prevents untranslated text flash during initialization
        if (!this.storageService.language) {
            this.selectedLanguage = 'en-US';
        }
        else {
            this.selectedLanguage = this.storageService.language;
        }

        // Only sync with app component if no cached language exists
        // This prevents overriding user dropdown selections
        if (!this.storageService.language) {
            this.multilingualService.currentLanguage.subscribe((currentLang) => {
                if (currentLang) {
                    this.selectedLanguage = currentLang;
                }
            });
        }

        this.multilingualService.availableLanguage.subscribe((resp) => {
            this.availableLanguage = resp;

            if (!this.storageService.language) {
                resp.find((item) => {
                    if (item.isDefault) {
                        this.selectedLanguage = item.formattedLocal;
                        this.storageService.setLanguage(this.selectedLanguage);
                        this.translateService.use(this.selectedLanguage);
                    }
                });
            }
        });
    }

    private initHeaderUI(): void {
        this.updateDemoButtonVisibilityState();
    }

    private async getUserDetails(): Promise<void> {
        try {
            const resp = await firstValueFrom(this.accountService.getUserDetails());
            if (resp) {
                this.userDetails = resp;
            }
        } catch (error) {
            // Error will be handled by caller or logged
            throw error;
        }
    }

    private updateDemoButtonVisibilityState() {
        // Show "Book A Demo" only on login page
        // Hide on verify/devices route (auth-flow scenario for device limit exceeded)
        const currentUrl = this.router.url.split('/');
        const currentRoute = currentUrl[currentUrl.length - 1];
        const isVerifyRoute = this.router.url.includes('/verify');
        this.showDemoButton = currentRoute.toLowerCase() === 'login' && !isVerifyRoute;
    }

    private getUnreadNotificationCount(): void {
        this.notificationService.getNotificationCount().subscribe({
            next: (resp) => {
                this.unreadNotificationCount = resp.result.unreadCount;
            },
            error: (err) => {
                this.errorService.showError(err);
            },
        });
    }

    private initFirstTimeJourneyUi(): void {
        this.activatedRoute.queryParams.subscribe((params) => {
            const firstTimeJourneyParam = params['firstTimeJourney'];

            this.firstTimeJourneyStepsParam = params['steps'];
            this.isFirstTimeJourney = firstTimeJourneyParam === 'true';
            
            if (this.isClientSide() && typeof window !== 'undefined') {
                this.isMobileMenuOpen = window.innerWidth < SCREEN_WIDTH_DESKTOP && this.isFirstTimeJourney;

                if (Number(this.firstTimeJourneyStepsParam) >= JourneyListSequence.Search) {
                    this.isMobileMenuOpen = false;
                }
            }
        });
    }

    private registerMenuTriggerListener(): void {
        this.menuTriggerService.menuState$.subscribe((isOpen) => {
            if (!this.menuTrigger) {
                return;
            }

            if (isOpen) {
                this.menuTrigger.openMenu();
            } else {
                this.menuTrigger.closeMenu();
            }
        });
    }
}
